import React from 'react';
import {
  animate,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import Icon from '../../../Assets/Icon/CustomIcon';
import { buildSubtitle, formatPhoneNumber, getNameInitial } from './emTelUtils';

const SWIPE_ACTION_WIDTH = 80;
const SWIPE_OPEN_X = -SWIPE_ACTION_WIDTH;
const SWIPE_SNAP_SPRING = {
  type: 'spring',
  stiffness: 380,
  damping: 34,
  mass: 0.82,
};

function resolveSnapTarget(currentX, velocityX) {
  if (velocityX < -280) return SWIPE_OPEN_X;
  if (velocityX > 280) return 0;
  if (currentX < SWIPE_OPEN_X * 0.42) return SWIPE_OPEN_X;
  return 0;
}

function SwipeableRow({ children, onRemove, item }) {
  const x = useMotionValue(0);

  const actionX = useTransform(x, (value) =>
    Math.max(0, SWIPE_ACTION_WIDTH + value)
  );
  const actionOpacity = useTransform(
    x,
    [0, -12, -SWIPE_ACTION_WIDTH],
    [0, 0.55, 1]
  );
  const cardRadius = useTransform(
    x,
    [SWIPE_OPEN_X, -1, 0],
    ['16px 0 0 16px', '16px 0 0 16px', '16px']
  );

  const snapTo = (target, velocity = 0) => {
    animate(x, target, {
      ...SWIPE_SNAP_SPRING,
      velocity,
    });
  };

  const handleDragEnd = (_, info) => {
    snapTo(resolveSnapTarget(x.get(), info.velocity.x), info.velocity.x);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#f8faf9]">
      <motion.button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          snapTo(0);
          onRemove?.(item);
        }}
        className="absolute inset-y-0 right-0 z-0 flex w-20 flex-col items-center justify-center gap-1 bg-[#FF3B30] text-white active:bg-[#D32F2F]"
        style={{
          x: actionX,
          opacity: actionOpacity,
        }}
        aria-label="비상연락망 해제"
      >
        <Icon.Trash width="18" height="18" color="#fff" />
        <span className="text-xs font-semibold">해제</span>
      </motion.button>

      <motion.div
        className="relative z-10"
        style={{
          x,
          borderRadius: cardRadius,
          touchAction: 'pan-y',
        }}
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: SWIPE_OPEN_X, right: 0 }}
        dragElastic={{ left: 0.1, right: 0.08 }}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        onClick={() => {
          if (x.get() < -8) snapTo(0);
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function EmTelContactCard({
  item,
  onCall,
  onEdit,
  onResumeApprovalChange,
  showEdit = false,
}) {
  const subtitle = buildSubtitle(item);
  const initial = getNameInitial(item.manageName);
  const isResumeApprover =
    String(item.resumeApprovalYn ?? '').toUpperCase() === 'Y';

  const cardContent = (
    <div className="flex items-center gap-3.5 border border-[#dee2e6]/30 bg-white p-4 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_4px_6px_-2px_rgba(0,0,0,0.05)]">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white bg-[#e9f5eb] shadow-sm">
        {item.photo || item.managePhoto ? (
          <img
            src={item.photo || item.managePhoto}
            alt={item.manageName}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-base font-bold text-[#34a853]">{initial}</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex min-w-0 items-center gap-2 overflow-hidden">
          <h3 className="min-w-0 truncate text-md font-semibold text-[#1b1c1c]">
            {item.manageName || '-'}
          </h3>
          {item.rank && (
            <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#fdf2e9] p-2.5 text-[12px] font-semibold leading-none text-[#873a1a]">
              {item.rank}
            </span>
          )}
        </div>
        {subtitle && <p className="text-xs text-[#5f6368]">{subtitle}</p>}
        <p className="mt-1 text-sm tracking-tight text-[#5f6368]">
          {formatPhoneNumber(item.manageTel) || '-'}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {showEdit && onResumeApprovalChange && (
          <button
            type="button"
            title={
              isResumeApprover
                ? '작업재개 승인 권한 해제'
                : '작업재개 승인 권한 부여'
            }
            aria-label={
              isResumeApprover
                ? '작업재개 승인 권한 해제'
                : '작업재개 승인 권한 부여'
            }
            aria-pressed={isResumeApprover}
            onClick={(event) => {
              event.stopPropagation();
              onResumeApprovalChange(item, isResumeApprover ? 'N' : 'Y');
            }}
            onPointerDown={(event) => event.stopPropagation()}
            className={`flex items-center justify-center rounded-xl border p-2.5 transition-colors ${
              isResumeApprover
                ? 'border-[#34a853] bg-[#e9f5eb] text-[#34a853] shadow-[0_0_0_1px_rgba(52,168,83,0.15)]'
                : 'border-[#dee2e6]/50 text-[#9aa0a6] hover:border-[#34a853]/40 hover:text-[#34a853]'
            }`}
          >
            <Icon.TaskAlt width="20" height="20" />
          </button>
        )}
        <button
          type="button"
          disabled={!item.manageTel}
          onClick={(event) => {
            event.stopPropagation();
            onCall?.(item.manageTel);
          }}
          onPointerDown={(event) => event.stopPropagation()}
          className="flex shrink-0 items-center justify-center rounded-xl border border-[#dee2e6]/50 p-2.5 text-[#5f6368] transition-colors hover:text-[#34a853] disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="전화 걸기"
        >
          <Icon.Phone width="20" height="20" />
        </button>
      </div>
    </div>
  );

  if (!showEdit) {
    return (
      <div className="overflow-hidden rounded-2xl">{cardContent}</div>
    );
  }

  return (
    <SwipeableRow onRemove={onEdit} item={item}>
      {cardContent}
    </SwipeableRow>
  );
}
